import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from './common/Card'; 
import { Badge } from './common/Badge';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

// Define lead status types
type LeadStatus = 'new' | 'inProgress' | 'contacted' | 'qualified' | 'closed';

// Define the project interface
interface Project {
  id: string;
  title: string;
  authority: string;
  publication_date: string;
  country: string;
  description: string;
  ted_link: string;
  status: LeadStatus;
}

export function Leads() {
  const [leads, setLeads] = useState<Project[]>([]);
  
  useEffect(() => {
    // Load leads from localStorage on component mount
    const savedLeads = localStorage.getItem('projectLeads');
    if (savedLeads) {
      try {
        setLeads(JSON.parse(savedLeads));
      } catch (e) {
        console.error("Failed to parse leads from localStorage", e);
        localStorage.removeItem('projectLeads'); // Clear corrupted data
        setLeads([]); // Set to empty array if parsing fails
      }
    } else {
      setLeads([]); // Initialize with empty array if nothing in localStorage
    }

    // Add event listener to update leads when localStorage changes
    const handleStorageChange = (event: StorageEvent) => {
       // Only update if the 'projectLeads' key changed
       if (event.key === 'projectLeads') {
          const updatedSavedLeads = localStorage.getItem('projectLeads');
          if (updatedSavedLeads) {
            try {
              setLeads(JSON.parse(updatedSavedLeads));
            } catch (e) {
              console.error("Failed to parse leads from localStorage on update", e);
            }
          } else {
            setLeads([]);
          }
       }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Move a lead to a different status
  const updateLeadStatus = (leadId: string, newStatus: LeadStatus) => {
    const updatedLeads = leads.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    );
    setLeads(updatedLeads);
    localStorage.setItem('projectLeads', JSON.stringify(updatedLeads));
    // Dispatch a custom event that includes the key, useful for the listener
    window.dispatchEvent(new StorageEvent('storage', { key: 'projectLeads' }));
  };
  
  // Function to remove a lead
  const removeLead = (leadId: string) => {
    const filteredLeads = leads.filter(lead => lead.id !== leadId);
    setLeads(filteredLeads);
    localStorage.setItem('projectLeads', JSON.stringify(filteredLeads));
     // Dispatch a custom event that includes the key, useful for the listener
    window.dispatchEvent(new StorageEvent('storage', { key: 'projectLeads' }));
  };
  
  // Get leads by status
  const getLeadsByStatus = (status: LeadStatus) => {
    return leads.filter(lead => lead.status === status);
  };
  
  // Handle drag end event - update the lead status when dropped in a different column
  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // If there's no destination or user dropped item back in same place
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) return;
    
    // Get the new status from the destination droppableId
    const newStatus = destination.droppableId as LeadStatus;
    
    // Update the lead's status
    updateLeadStatus(draggableId, newStatus);
  };
  
  // Status column titles in German
  const statusTitles: Record<LeadStatus, string> = {
    new: 'Neu',
    inProgress: 'In Bearbeitung',
    contacted: 'Kontaktiert',
    qualified: 'Qualifiziert',
    closed: 'Abgeschlossen',
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold tracking-tight">Projekt Leads</h2>
      </div>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 overflow-x-auto pb-4">
          {(['new', 'inProgress', 'contacted', 'qualified', 'closed'] as LeadStatus[]).map((status) => (
            <div key={status} className="w-full flex flex-col">
              <Card className="h-full flex flex-col bg-muted/30">
                <CardHeader className="pb-1 pt-2 px-2">
                  <CardTitle className="flex items-center justify-between text-base font-semibold">
                    {statusTitles[status]}
                    <Badge variant="secondary" className="ml-2">
                      {getLeadsByStatus(status).length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <Droppable droppableId={status}>
                  {(provided) => (
                    <div 
                      className="flex-1 overflow-y-auto p-3 space-y-4 max-h-[calc(100vh-250px)]"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {getLeadsByStatus(status).map((lead, index) => (
                        <Draggable key={lead.id} draggableId={lead.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.6 : 1
                              }}
                              className="mb-3"
                            >
                              <Card className="p-4 bg-card shadow-sm hover:shadow-md transition-shadow relative w-full">
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="absolute top-2 right-2 text-muted-foreground hover:text-destructive bg-transparent hover:bg-transparent p-0 w-6 h-6 flex items-center justify-center border-0 shadow-none" 
                                  onClick={() => removeLead(lead.id)}
                                  aria-label={`Entferne Lead ${lead.title}`}
                                >
                                  <span className="text-lg">✕</span>
                                </Button>
                                <h4 className="font-medium text-base leading-tight mb-3 line-clamp-2 pr-8 mt-4">{lead.title}</h4>
                                <div className="flex flex-col space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Badge variant="outline" className="px-2 py-0.5">{lead.country}</Badge>
                                    <span>{new Date(lead.publication_date).toLocaleDateString('de-DE')}</span>
                                  </div>
                                  <div className="flex flex-col space-y-2 mt-2">
                                    <Badge variant="secondary" className="px-2 py-1 self-start">{lead.authority}</Badge>
                                    <div className="flex gap-3 text-sm">
                                      <a 
                                        href={lead.ted_link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                        aria-label={`Originalartikel für ${lead.title} ansehen`}
                                      >
                                        Artikel
                                      </a>
                                      <Link 
                                        to={`/analysis#${encodeURIComponent(lead.id)}`} 
                                        className="text-primary hover:underline"
                                        aria-label={`Analysedetails für ${lead.title} ansehen`}
                                      >
                                        Details
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {getLeadsByStatus(status).length === 0 && (
                        <div className="text-center py-4 text-muted-foreground text-sm">
                          Keine Leads
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </Card>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
} 